import { apiClient } from '@/utils/apiClient';
import { AppointmentStatus } from '@/utils/enums/Consults';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useConsultsStore } from '@/utils/stores/useConsultsStore';
import { useExpertsStore } from '@/utils/stores/useExpertsStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { CalendlyAppointment } from '../types/CalendlyAppointment';
import { filterAppointments } from '../utils/filterAppointments';
import { parseAppointments } from '../utils/parseAppointments';

export const useGetAppointments = () => {
    const { auth } = useAuthStore();
    const idToken = auth.idToken;
    const { userData } = useUserStore();
    const email = userData.email;

    const { experts } = useExpertsStore();
    const { specialist, physician, nutritionist } = experts;

    const { updateConsultsData } = useConsultsStore();

    const getAppointments = async (idToken: string, email: string): Promise<CalendlyAppointment[] | null> => {
        try {
            const data = await apiClient.get(`/api/booking/appointments/${email}`, idToken);
            return data;
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("Session expired")) {
                    throw new Error("Session expired");
                }
            }
            throw new Error("An unexpected error occurred");
        }
    };

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['appointments', email, idToken],
        queryFn: () => getAppointments(idToken!, email!),
        enabled: !!idToken && !!email,
        refetchOnWindowFocus: true,
        staleTime: 60 * 1000,
    });

    const sessionExpired = error instanceof Error && error.message === "Session expired";

    useEffect(() => {
        if (data) {
            const { allAppointments } = parseAppointments(data, specialist, nutritionist, physician);

            const active = filterAppointments.byStatus(allAppointments, AppointmentStatus.Active);
            const future = active.filter(filterAppointments.isFuture);
            const past = active.filter(filterAppointments.isPast);
            const nutritionistFuture = future.filter(filterAppointments.isNutritionist);
            const expertFuture = future.filter(filterAppointments.isExpert);

            updateConsultsData({
                allAppointments: active,
                allFutureAppointments: future,
                allPastAppointments: past,
                nutritionistFutureConsults: nutritionistFuture.length ? nutritionistFuture : null,
                specialistFutureConsults: expertFuture.length ? expertFuture : null,
                mostRecentFutureAppointment: future[0] || null,
            });
        }
    }, [data, specialist, nutritionist, physician, updateConsultsData]);

    useEffect(() => {
        if (error instanceof Error && error.message === "Session expired") {
            console.warn("Token expired, logging out...");
            // clear local auth store, redirect, or show modal
            // e.g.
            // useAuthStore.getState().logout();
            // navigate("/login");
        }
    }, [error]);

    return {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
        sessionExpired,
    };
};